import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect
} from "react-router-dom";
import { Provider, connect } from "react-redux";
import styled, { ThemeProvider } from "styled-components";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { datadogLogs } from "@datadog/browser-logs";
import { FatalError, Loading } from "@onelogin/react-components";
import ErrorBoundary from "./ErrorBoundary";
import AppContent from "./AppContent";
import LegacyRedirect from "./LegacyRedirect";
import SecurityQuestionsContent from "./screens/ForcedSecurityQuestions/SecurityQuestionsContent";
import OneTrust from "./OneTrust";
import { otpPromptCheck } from "../api/profileServiceApi";
import AccessDenied from "./common/ErrorScreen/AccessDenied";
import { PUBLIC_URL, LEGACY_URL } from "../constants/Env";

const FatalErrorWrapper = styled.div`
  width: 100%;
`;

const App = ({ store, theme, fatalErrorMessage }) => {
  const account = store?.getState()?.account || {};
  const [isLoading, setIsLoading] = useState(true);
  const [isUserForbidden, setIsUserForbidden] = useState(false);
  const [forbiddenMessage, setForbiddenMessage] = useState("");

  useEffect(() => {
    const checkOtpPrompt = async () => {
      try {
        const data = await otpPromptCheck("profile");
        if (data?.otp_auth_required) {
          window.location.replace(
            data.redirect_to ||
            `/login2/?return=${encodeURIComponent(data.return)}`
          );
        }
      } catch (error) {
        const errMessage = error?.message || "An unexpected error occurred";
        setIsUserForbidden(true);
        setForbiddenMessage(errMessage);
        datadogLogs.logger.error(
          "An error occurred in web-profile",
          {
            message: errMessage
          },
          error
        );
      } finally {
        setIsLoading(false);
      }
    };

    checkOtpPrompt();
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  if (isUserForbidden) {
    return (
      <FatalErrorWrapper>
        <AccessDenied
          message={forbiddenMessage}
        />
      </FatalErrorWrapper>
    );
  }

  return (
    <ErrorBoundary>
      <OneTrust cookieManagementEnabled={account.cookieManagementEnabled}>
        <ThemeProvider theme={theme}>
          <Provider store={store}>
            {fatalErrorMessage ? (
              <FatalErrorWrapper>
                <FatalError
                  message={<FormattedMessage {...fatalErrorMessage} />}
                />
              </FatalErrorWrapper>
            ) : (
              <Router>
                <Switch>
                  <Route
                    path={`${PUBLIC_URL}/security_questions`}
                    component={SecurityQuestionsContent}
                  />
                  <Route path={`${LEGACY_URL}`} component={LegacyRedirect} />
                  <Route path={`${PUBLIC_URL}`} component={AppContent} />
                  <Redirect path="*" to={`${PUBLIC_URL}`} />
                </Switch>
              </Router>
            )}
          </Provider>
        </ThemeProvider>
      </OneTrust>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  theme: state.branding,
  fatalErrorMessage: state.fatalError.fatalErrorMessage
});

App.propTypes = {
  theme: PropTypes.shape({
    accentColor: PropTypes.string.isRequired,
    loaded: PropTypes.bool,
    logoUrl: PropTypes.string,
    primaryColor: PropTypes.string.isRequired,
    primaryFontColor: PropTypes.string.isRequired
  })
};

export default connect(mapStateToProps)(App);
